/*
 * Copyright (c) 2024. Tooling Team
 */

import { useState } from "react";
import HeaderService from "../services/headerService";
import { useLazyGetConceptDataQuery } from "../app/services/conceptsApi";
import { useLazyGetMapByIdQuery } from "../app/services/mapsApi";
import {
  useLazyGetMapTagsQuery,
  useLazyGetMapValueTagQuery,
} from "../app/services/tagsApi";
import { useDispatch, useSelector } from "react-redux";
import {
  setTableLoading,
  setIsSearchDisabled,
} from "../app/slices/EIPMapValueDefinition.slice";
import { transformMVApiResponse } from "../utils/funcs";
import { applyMapDetailsToState } from "../utils/mapDetails";

export default () => {
  const {
    mapVersion,
    mapVersionList,
    mapValueTags,
    mapStatus,
    isEditableVersion,
    mapTags,
    mapAttributes,
    allTags,
    mapID,
    conceptSetData,
    tableLoading,
    isSearchDisabled,
    rowData,
    mapColumns,
  } = useSelector((state) => state.map || {});
  const dispatch = useDispatch();
  const [isSearchBtnDisabled, setIsSearchBtnDisabled] = useState(true);
  const headerService = new HeaderService();
  const [mapByIdApi] = useLazyGetMapByIdQuery();
  const [conceptSetDataApi] = useLazyGetConceptDataQuery();
  const [mapValueTagApi] = useLazyGetMapValueTagQuery();
  const [mapTagsApi] = useLazyGetMapTagsQuery();

  async function fetchMapById(mapID, callbackToResetActionButtons) {
    dispatch(setTableLoading(true));

    try {
      // Fetch the map object
      const mapByApiResponse = await mapByIdApi({ mapID }).unwrap();
      const data = mapByApiResponse
        ? JSON.parse(JSON.stringify(mapByApiResponse))
        : {};

      await conceptSetDataApi(data.conceptSetId).unwrap();

      await mapValueTagApi(data.uuid).unwrap();

      // Table loading finish
      dispatch(setTableLoading(false));
      dispatch(setIsSearchDisabled(false));
      //data is passed directly here to avoid issues with async state variables
      callbackToResetActionButtons(data.status, data.isEditableVersion);
    } catch (error) {
      console.log("on error", error);
    } finally {
      dispatch(setTableLoading(false));
    }
  }

  //For use in Standard Code System = ALL fetches
  async function fetchMapsByIds(mapIDs, callbackToResetActionButtons) {
    dispatch(setTableLoading(true));

    try {
      const mapResponses = await Promise.all(
        mapIDs.map((mapID) => mapByIdApi({ mapID }).unwrap())
      );

      const mapDataList = mapResponses.map((response) =>
        response ? JSON.parse(JSON.stringify(response)) : {}
      );
      const firstMap = mapDataList[0];
      if (!firstMap) {
        dispatch(setTableLoading(false));
        dispatch(setIsSearchDisabled(false));
        return;
      }

      const combinedRecords = [];
      mapDataList.forEach((mapData) => {
        const records = mapData.mapElements?.records || [];
        transformMVApiResponse(records);
        combinedRecords.push(...records);
      });

      applyMapDetailsToState(dispatch, firstMap, {
        recordsOverride: combinedRecords,
        shouldTransform: false,
      });
      await conceptSetDataApi(firstMap.conceptSetId).unwrap();
      await mapValueTagApi(firstMap.uuid).unwrap();

      dispatch(setTableLoading(false));
      dispatch(setIsSearchDisabled(false));
      if (callbackToResetActionButtons) {
        callbackToResetActionButtons(
          firstMap.status,
          false //ALL load is currently read-only
        );
      }
    } catch (error) {
      console.log("on error", error);
      dispatch(setIsSearchDisabled(false));
      throw error;
    } finally {
      dispatch(setTableLoading(false));
    }
  }

  async function fetchAllTags() {
    await mapTagsApi().unwrap();
  }

  // Finally, it exposes only what is required by the component
  return {
    state: {
      conceptSetData,
      mapValueTags,
      mapStatus,
      mapVersion,
      mapTags,
      mapVersionList,
      isEditableVersion,
      allTags,
      rowData,
      mapColumns,
      mapAttributes,
      tableLoading,
      isSearchDisabled,
      setIsSearchDisabled,
      isSearchBtnDisabled,
      setIsSearchBtnDisabled,
      mapID,
    },
    fetchMapById,
    fetchMapsByIds,
    fetchAllTags,
    headerService,
  };
};
